/**
 * MouseInput.js - Mouse Input Handler
 *
 * Handles mouse input events and state tracking.
 * Provides:
 * - Mouse position tracking
 * - Mouse button state
 * - Aim angle calculation
 * - World coordinate conversion
 */

const MouseInput = {
  // Mouse state
  state: {
    x: 0,
    y: 0,
    down: false,
    angle: 0
  },

  // Canvas reference for coordinate calculations
  canvas: null,

  // Callbacks for mouse events
  callbacks: {
    onMouseDown: null,
    onMouseUp: null,
    onMouseMove: null,
    onWheel: null
  },

  // World coordinate tracking (for debug mode)
  worldX: 0,
  worldY: 0,

  /**
   * Initialize mouse input handling
   * @param {Object} options - Configuration options
   * @param {HTMLCanvasElement} options.canvas - Game canvas
   * @param {Function} options.onMouseDown - Mouse down callback
   * @param {Function} options.onMouseUp - Mouse up callback
   * @param {Function} options.onMouseMove - Mouse move callback
   * @param {Function} options.onWheel - Mouse wheel callback
   */
  init(options = {}) {
    this.canvas = options.canvas || document.getElementById('gameCanvas');
    this.callbacks.onMouseDown = options.onMouseDown || null;
    this.callbacks.onMouseUp = options.onMouseUp || null;
    this.callbacks.onMouseMove = options.onMouseMove || null;
    this.callbacks.onWheel = options.onWheel || null;

    // Bind event listeners
    document.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    document.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    document.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
  },

  /**
   * Handle mousedown event
   * @param {MouseEvent} e - Mouse event
   */
  handleMouseDown(e) {
    if (e.button !== 0) return; // Left button only

    this.state.down = true;

    // Call callback if registered
    if (this.callbacks.onMouseDown) {
      this.callbacks.onMouseDown(e, this.state);
    }
  },

  /**
   * Handle mouseup event
   * @param {MouseEvent} e - Mouse event
   */
  handleMouseUp(e) {
    if (e.button !== 0) return; // Left button only

    this.state.down = false;

    // Call callback if registered
    if (this.callbacks.onMouseUp) {
      this.callbacks.onMouseUp(e, this.state);
    }
  },

  /**
   * Handle mousemove event
   * @param {MouseEvent} e - Mouse event
   */
  handleMouseMove(e) {
    if (!this.canvas) return;

    const rect = this.canvas.getBoundingClientRect();

    // Get mouse position relative to canvas display area (CSS pixels / screen space)
    // PixiJS with autoDensity: true handles devicePixelRatio internally,
    // so we use screen-space coordinates directly - no scaling needed!
    const mx = Math.round(e.clientX - rect.left);
    const my = Math.round(e.clientY - rect.top);

    // Use screen-space center (CSS dimensions, not internal canvas dimensions)
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const dx = mx - centerX;
    const dy = my - centerY;

    // Update state with screen-space coordinates
    this.state.x = mx;
    this.state.y = my;
    this.state.angle = Math.atan2(dy, dx);

    // Store screen dimensions for world coordinate calculations
    this._screenWidth = rect.width;
    this._screenHeight = rect.height;

    // Call callback if registered
    if (this.callbacks.onMouseMove) {
      this.callbacks.onMouseMove(e, this.state);
    }
  },

  /**
   * Handle mouse wheel event
   * @param {WheelEvent} e - Wheel event
   */
  handleWheel(e) {
    // Prevent default scrolling behavior
    e.preventDefault();

    // Determine scroll direction
    // Positive deltaY = scroll down = next slot (right)
    // Negative deltaY = scroll up = previous slot (left)
    const direction = e.deltaY > 0 ? 1 : -1;

    // Call callback if registered
    if (this.callbacks.onWheel) {
      this.callbacks.onWheel(direction, e);
    }
  },

  /**
   * Update world coordinates based on player position
   * @param {number} playerX - Player X position
   * @param {number} playerY - Player Y position
   */
  updateWorldCoordinates(playerX, playerY) {
    if (!this.canvas) return;

    // Use screen-space center (stored from last mouse move)
    // Fall back to canvas bounding rect if not yet available
    const centerX = this._screenWidth ? this._screenWidth / 2 : this.canvas.getBoundingClientRect().width / 2;
    const centerY = this._screenHeight ? this._screenHeight / 2 : this.canvas.getBoundingClientRect().height / 2;

    this.worldX = Math.round(this.state.x - centerX + playerX);
    this.worldY = Math.round(this.state.y - centerY + playerY);
  },

  /**
   * Check if mouse button is down
   * @returns {boolean}
   */
  isMouseDown() {
    return this.state.down;
  },

  /**
   * Get current aim angle
   * @returns {number} - Angle in radians
   */
  getAngle() {
    return this.state.angle;
  },

  /**
   * Get mouse position
   * @returns {{x: number, y: number}}
   */
  getPosition() {
    return { x: this.state.x, y: this.state.y };
  },

  /**
   * Get world coordinates
   * @returns {{x: number, y: number}}
   */
  getWorldPosition() {
    return { x: this.worldX, y: this.worldY };
  },

  /**
   * Get the full mouse state (for backward compatibility)
   * @returns {Object}
   */
  getState() {
    return this.state;
  }
};

// Export for use in other modules
// For now, MouseInput is a global object loaded via script tag

export { MouseInput };
